/**
 * Fixed point: the sim keeps the amounts that change every tick as integers
 * in thousandths (`MILLI` per whole unit), never as floats, so every peer's
 * state matches exactly and snapshots and their deltas carry short integers
 * rather than float noise:
 *
 * - hp (`Unit.hp`, `Building.hp`; `unitMaxHp`, `buildingMaxHp`, shot damage),
 * - stock (`Building.stock`, `playerStock`, plunder and raze amounts),
 * - carried loads (`Carry.amount`) and wild tiles' resources left
 *   (`tileResourceOverride`, `tileResources`),
 * - step progress (`Unit.progress`: thousandths of a step).
 *
 * Rules stay in whole units (hp, costs, rates); a value computed from them
 * becomes thousandths once, with `toMilli`, where it meets the state. The UI
 * shows `fromMilli` of these.
 */
import type { ResourceId } from './types';

/** Thousandths per whole unit. */
const MILLI = 1000;

/** `value` (whole units, e.g. from rules) in thousandths, rounded to the nearest. */
function toMilli(value: number): number {
  return Math.round(value * MILLI);
}

/** Thousandths back to whole units (for display, scores and rules-unit maths). */
function fromMilli(value: number): number {
  return value / MILLI;
}

/** A cost or amounts in whole units (rules) as thousandths, for the stock functions. */
function milliCost(cost: Readonly<Record<ResourceId, number>>): Record<ResourceId, number> {
  const out: Record<ResourceId, number> = {};
  for (const [res, amount] of Object.entries(cost)) out[res] = toMilli(amount);
  return out;
}

/** Amounts in thousandths as whole units (for display). */
function unitAmounts(amounts: Readonly<Record<ResourceId, number>>): Record<ResourceId, number> {
  const out: Record<ResourceId, number> = {};
  for (const [res, amount] of Object.entries(amounts)) out[res] = fromMilli(amount);
  return out;
}

export { fromMilli, MILLI, milliCost, toMilli, unitAmounts };
